import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { DateInput } from "@/components/admin/date-input";
import { formatBRL } from "@/lib/format";

import type { PassoDoFormulario } from "../../commons/JornadaDoFormulario";
import { useWatch } from "react-hook-form";
import { Bloco, CamposDaAdn, CampoInteiro, CampoReais, Linhas, Soma, useCalculoDaAdn } from "./CamposDaAdn";
import { projetarPeloIpca } from "./calculoAdn";
import { useIndicadoresMercado } from "../../consortium-groups/premissasMercado";

const A = "details.adn";

/**
 * "Determinação de necessidades — resumo" da planilha: o capital que o seguro
 * precisa cobrir, item por item, com o total que vira o argumento da proposta.
 */
const Necessidades = () => {
  const { necessidades: n } = useCalculoDaAdn();
  const linhas: [string, number][] = [
    ["Educação dos filhos", n.educacao],
    ["Padrão de vida em caso de invalidez", n.invalidez],
    ["Padrão de vida em caso de doenças graves", n.doencas],
    ["Quitação de dívidas", n.dividas],
    ["Custo do inventário", n.inventario],
  ];
  return (
    <section className="rounded-md border bg-card p-3">
      <h3 className="text-sm font-semibold">Determinação de necessidades</h3>
      <dl className="mt-2 divide-y text-sm tabular-nums">
        {linhas.map(([rotulo, valor]) => (
          <div key={rotulo} className="flex justify-between gap-4 py-1.5">
            <dt className="text-muted-foreground">{rotulo}</dt>
            <dd>{formatBRL(valor)}</dd>
          </div>
        ))}
        <div className="flex justify-between gap-4 pt-2 font-semibold">
          <dt>Capital necessário</dt>
          <dd>{formatBRL(n.total)}</dd>
        </div>
      </dl>
    </section>
  );
};

/**
 * Aba "Cálculo" da planilha: o plano proposto (mensal, prazo) e os valores de
 * resgate e patrimônio, corrigidos pelo IPCA até o fim do prazo.
 */
const Projecao = () => {
  const p = (useWatch({ name: `${A}.projecao` }) ?? {}) as Record<string, number | null>;
  // Sem IPCA digitado, vale o IPCA de 12 meses do Banco Central — a mesma
  // fonte da apresentação ("Por que consórcio"), não os 8% fixos da planilha.
  const { ipca12m } = useIndicadoresMercado();
  const digitado = p.ipca_pct != null && String(p.ipca_pct) !== "";
  const r = projetarPeloIpca({ ...p, ipca: (digitado ? Number(p.ipca_pct) : ipca12m.valor) / 100 });
  const pct = ipca12m.valor.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  return (
    <Bloco
      titulo="Projeção pelo IPCA"
      rodape={
        <>
          <Soma rotulo="Pagamento" valor={r.pagamento} />
          <Soma rotulo="Resgate" valor={r.resgate} />
          <Soma rotulo="Patrimônio" valor={r.patrimonio} forte />
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <CampoReais source={`${A}.projecao.mensal`} label="Valor mensal do plano" />
        <CampoInteiro source={`${A}.projecao.anos`} label="Tempo de pagamento (anos)" min={0} />
        <CampoInteiro source={`${A}.projecao.ipca_pct`} label="IPCA ao ano (%)" min={0} max={100} />
        <span />
        <CampoReais source={`${A}.projecao.resgate`} label="Resgate" />
        <CampoReais source={`${A}.projecao.patrimonio`} label="Patrimônio" />
      </div>
      <p className="text-[13px] text-muted-foreground">
        {digitado
          ? "IPCA informado manualmente."
          : `IPCA de 12 meses: ${pct}% (${
              ipca12m.fonte === "padrao" ? "referência" : "Banco Central"
            }, ${ipca12m.data}).`}
      </p>
    </Bloco>
  );
};

const DECISAO = ["Titular", "Cônjuge", "Em conjunto"];

const Fechamento = () => (
  <>
    <Bloco titulo="Feedback">
      <TextInput source={`${A}.feedback.necessidades`} label="Concordância com as necessidades não atendidas" helperText={false} />
      <TextInput source={`${A}.feedback.preocupacao`} label="Necessidade de proteção que mais preocupa" helperText={false} />
      <TextInput source={`${A}.feedback.recomendacao`} label="Importância da recomendação recebida" helperText={false} />
      <TextInput source={`${A}.feedback.sentido`} label="Avaliação da primeira etapa do planejamento" helperText={false} />
      <TextInput source={`${A}.feedback.indicaria`} label="Indicaria o trabalho para quem" helperText={false} />
    </Bloco>

    <Bloco titulo="Indicações">
      <Linhas source={`${A}.indicacoes`} rotuloAdicionar="Indicação" novo={{ nome: "", telefone: "" }}>
        {(p) => (
          <>
            <TextInput source={`${p}.nome`} label="Nome" helperText={false} />
            <TextInput source={`${p}.telefone`} label="Telefone" helperText={false} />
            <TextInput source={`${p}.casado`} label="Estado civil" helperText={false} />
            <TextInput source={`${p}.filhos`} label="Filhos" helperText={false} />
            <TextInput source={`${p}.salario`} label="Salário" helperText={false} />
            <TextInput source={`${p}.trabalho`} label="Tipo de trabalho" helperText={false} />
            <TextInput source={`${p}.hobby`} label="Hobby" helperText={false} />
            <TextInput source={`${p}.historia`} label="Apelido ou história" helperText={false} />
          </>
        )}
      </Linhas>
    </Bloco>

    <Bloco titulo="Apresentação do plano">
      <div className="grid gap-3 sm:grid-cols-2">
        <DateInput source={`${A}.apresentacao_data`} label="Data" helperText={false} />
        <TextInput source={`${A}.apresentacao_hora`} label="Horário" type="time" helperText={false} />
        <SelectInput
          source={`${A}.decisao_investimentos`}
          label="Decisão de investimentos"
          choices={DECISAO.map((x) => ({ id: x, name: x }))}
          helperText={false}
        />
        <CampoReais
          source={`${A}.valor_disposto`}
          label="Valor disposto a investir por mês"
          helperText="Para proteger 100% das necessidades levantadas."
        />
      </div>
    </Bloco>
  </>
);

/**
 * Os dois passos que a ficha ganha quando o assunto é seguro de vida: o
 * mapeamento (ADN) e o resumo com o capital necessário e o fechamento da
 * entrevista. Dois e não sete: a trilha de passos já tem os da ficha, e sete
 * círculos a mais não cabem na largura do diálogo.
 */
export const passosDaAdn = (): PassoDoFormulario[] => [
  {
    id: "adn",
    rotulo: "ADN",
    titulo: "ADN: mapeamento do cliente",
    apoio: "Análise de necessidades para seguro de vida. Todos os campos opcionais.",
    conteudo: <CamposDaAdn />,
  },
  {
    id: "adn-resumo",
    rotulo: "Resumo",
    titulo: "Necessidades e fechamento",
    apoio: "Capital necessário calculado a partir da ADN.",
    conteudo: (
      <>
        <Necessidades />
        <Projecao />
        <Fechamento />
      </>
    ),
  },
];
